/** Shared Google Maps JS bootstrap (importLibrary + referrer-safe script load). */

let mapsLoader: Promise<void> | null = null
let loadedKey: string | null = null

/**
 * Google rejected this key for this site.
 *
 * The failure is asynchronous and one-way: the script loads, `importLibrary`
 * works, and then every callback-style API — Geocoder, PlacesService,
 * StreetViewService — simply never calls back. Callers that await those
 * callbacks hang forever unless they know an auth failure happened, so the
 * fact is recorded here rather than only rejecting the load promise (which may
 * already have resolved by the time Google decides).
 */
let authFailed = false

export function mapsAuthFailed(): boolean {
  return authFailed
}

const authFailureListeners = new Set<() => void>()

/**
 * Be told when Google rejects the key for this site.
 *
 * Google paints its own error panel inside the map's div and throws nothing,
 * so a map whose key is referrer-blocked looks loaded to the code that made it.
 * A component that wants to fall back to something else has to be called when
 * `gm_authFailure` fires; reading `mapsAuthFailed()` afterwards is not enough
 * because every `loadMapsScript` that has to reload resets that flag. Returns
 * the unsubscribe.
 */
export function onMapsAuthFailure(listener: () => void): () => void {
  authFailureListeners.add(listener)
  return () => {
    authFailureListeners.delete(listener)
  }
}

export function loadMapsScript(apiKey: string): Promise<void> {
  if (loadedKey === apiKey && typeof window.google?.maps?.importLibrary === 'function') {
    return Promise.resolve()
  }
  if (mapsLoader && loadedKey === apiKey) return mapsLoader

  const existing = document.querySelector<HTMLScriptElement>('script[data-helios-maps="1"]')
  if (existing && loadedKey && loadedKey !== apiKey) {
    existing.remove()
    mapsLoader = null
    loadedKey = null
  }

  authFailed = false
  mapsLoader = new Promise((resolve, reject) => {
    const ready = () => {
      const g = window.google
      if (!g?.maps?.importLibrary) {
        mapsLoader = null
        loadedKey = null
        reject(new Error('Google Maps loaded without importLibrary. Enable Maps JavaScript API on this key.'))
        return
      }
      loadedKey = apiKey
      resolve()
    }

    const already = typeof window.google?.maps?.importLibrary === 'function'
    if (already) {
      ready()
      return
    }

    const prevAuth = window.gm_authFailure
    window.gm_authFailure = () => {
      authFailed = true
      mapsLoader = null
      loadedKey = null
      prevAuth?.()
      for (const listener of [...authFailureListeners]) {
        try {
          listener()
        } catch {
          // One subscriber's fault must not stop the others learning of it.
        }
      }
      reject(
        new Error(
          'Google rejected this Maps key for this site. Enable Maps JavaScript API and check HTTP-referrer restrictions.',
        ),
      )
    }

    const callbackName = '__heliosMapsReady'
    ;(window as unknown as Record<string, unknown>)[callbackName] = () => {
      ready()
    }

    const script = document.createElement('script')
    script.dataset.heliosMaps = '1'
    script.async = true
    script.defer = true
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&loading=async&callback=${callbackName}`
    script.onerror = () => {
      mapsLoader = null
      loadedKey = null
      reject(new Error('Google Maps failed to load. Check the API key and that Maps JavaScript API is enabled.'))
    }
    document.head.appendChild(script)
  })
  return mapsLoader
}

export async function mapsLibraries() {
  const mapsNs = await google.maps.importLibrary('maps')
  const geocoding = await google.maps.importLibrary('geocoding').catch(() => null)
  const street = await google.maps.importLibrary('streetView').catch(() => null)
  const places = await google.maps.importLibrary('places').catch(() => null)
  const markerLib = await google.maps.importLibrary('marker').catch(() => null)
  return {
    Map: (mapsNs as google.maps.MapsLibrary).Map,
    MapTypeId: google.maps.MapTypeId,
    Geocoder: (geocoding as google.maps.GeocodingLibrary | null)?.Geocoder ?? google.maps.Geocoder,
    StreetViewService:
      (street as google.maps.StreetViewLibrary | null)?.StreetViewService ?? google.maps.StreetViewService,
    StreetViewPanorama:
      (street as google.maps.StreetViewLibrary | null)?.StreetViewPanorama ?? google.maps.StreetViewPanorama,
    StreetViewStatus: google.maps.StreetViewStatus,
    Marker: (markerLib as google.maps.MarkerLibrary | null)?.Marker ?? google.maps.Marker,
    places: (places as google.maps.PlacesLibrary | null) ?? google.maps.places,
  }
}
